// Monitoring section shared by musician and crew edit pages.
// Replaces the old separate IEM-Type select + Wedges select + Subwoofer
// checkbox with one clear fieldset: first pick whether the person uses
// in-ears at all, then the floor-wedge count and subwoofer option.
// Also lets you route the monitor OUTPUTS: which rack and starting channel
// the IEMs / wedges are patched out of, and whether multiple wedges are
// stereo (L/R) or linked (same mono feed).

import { useState } from 'react';
import type { StageRack } from '../types';
import { rackCapacities, isChannelTakenForOutput, type RackUsage } from '../lib/rackUsage';
import { useTranslation } from 'react-i18next';

export type IemChoice = 'none' | 'stereo' | 'mono';

interface MonitoringSectionProps {
  iemType: string;
  /** Wired in-ears (no transmitter needed). */
  iemWired?: boolean;
  /** WHO provides the wired pack: 'band' (default) or 'venue'. (2026-10-17) */
  iemWiredProvidedBy?: 'band' | 'venue';
  wedges: number;
  subwoofer: boolean;
  iemRackId?: string;
  iemOutStart?: number;
  wedgeRackId?: string;
  wedgeOutStart?: number;
  wedgesStereo?: boolean;
  subRackId?: string;
  subOutStart?: number;
  /** PATCH MARK per return group (2026-10-23, user) — the Output List tints
   *  the Ch cell(s) of the IEM / wedges / sub rows with it. */
  iemPatchMark?: string;
  wedgePatchMark?: string;
  subPatchMark?: string;
  racks?: StageRack[];
    usage?: RackUsage;
    /** Instrument names of the person being edited (replacement/guest sharing). */
        editedInstruments?: Set<string>;
        /** Map of every person's label → set of instrument names they play. */
        personInstruments?: Record<string, Set<string>>;
        /** Tags of the person being edited (replacement/guest sharing for crew). */
        editedTags?: Set<string>;
        /** Map of every person's label → set of tags they carry. */
        personTags?: Record<string, Set<string>>;
        /** Label of the person being edited — their OWN output channels must
         *  always be greyed out (they used to read as free). (2026-09-20) */
        personLabel?: string;
      onChange: (patch: {
    iemType?: IemChoice;
    iemWired?: boolean;
    iemWiredProvidedBy?: 'band' | 'venue';
    wedges?: number;
    subwoofer?: boolean;
    iemRackId?: string;
    iemOutStart?: number;
    wedgeRackId?: string;
    wedgeOutStart?: number;
    wedgesStereo?: boolean;
    subRackId?: string;
    subOutStart?: number;
    /** PATCH MARK (2026-10-23, user): colour per return group (IEM / wedges /
        sub) — the Output List tints the Ch cell(s) with it. undefined = clear. */
    iemPatchMark?: string;
    wedgePatchMark?: string;
    subPatchMark?: string;
  }) => void;
  /** Colours already used on the band's tracks/cards (favourites). */
  personMarks?: string[];
  /** RENAME a favourite (2026-10-23, user): recolour EVERY track / monitor
   *  return of the band that carries `from` to `to` (persisted everywhere). */
  onRenameFavourite?: (from: string, to: string) => void;
  }

const IEM_OPTIONS: { value: IemChoice; labelKey: string; hintKey: string }[] = [
  { value: 'none', labelKey: 'monitoring.noInEars', hintKey: 'monitoring.noInEarsHint' },
  { value: 'stereo', labelKey: 'monitoring.stereoInEars', hintKey: 'monitoring.stereoInEarsHint' },
  { value: 'mono', labelKey: 'monitoring.monoInEars', hintKey: 'monitoring.monoInEarsHint' },
];

const WEDGE_OPTIONS = [0, 1, 2, 3, 4];

export default function MonitoringSection({
  iemType,
  iemWired,
  iemWiredProvidedBy,
  wedges,
  subwoofer,
  iemRackId,
  iemOutStart,
  wedgeRackId,
  wedgeOutStart,
  wedgesStereo,
  subRackId,
  subOutStart,
  iemPatchMark,
  wedgePatchMark,
  subPatchMark,
  racks = [],
  usage,
  editedInstruments,
    personInstruments,
    editedTags,
    personTags,
    personLabel,
    personMarks,
    onRenameFavourite,
    onChange,
  }: MonitoringSectionProps) {
  const { t } = useTranslation();
  // PATCH MARK popover (2026-10-23, user): one colour picker shared by the 3
  // return groups (IEM / wedges / sub) — the swatch sits next to each group's
  // channel select; viewport-clamped like the track-grid popover.
  const [markPop, setMarkPop] = useState<{ group: string; x: number; y: number } | null>(null);
  const openMarkPop = (group: string, ev: React.MouseEvent<HTMLButtonElement>) => {
    const r = ev.currentTarget.getBoundingClientRect();
    const popW = 200, popH = 200;
    let x = r.left, y = r.bottom + 6;
    if (y + popH > window.innerHeight) y = Math.max(8, r.top - popH - 6);
    if (x + popW > window.innerWidth) x = Math.max(8, window.innerWidth - popW - 8);
    setMarkPop({ group, x, y });
  };
  const markSwatch = (group: string, current: string | undefined) => (
    <button
      type="button"
      className="td-patchmark"
      title={t('instrumentEditor.patchMarkTitle')}
      onClick={(e) => (markPop?.group === group ? setMarkPop(null) : openMarkPop(group, e))}
      style={{ width: 30, height: 30, padding: 0, border: '1px solid var(--border)', borderRadius: 4, background: current || 'var(--surface)', cursor: 'pointer', flexShrink: 0 }}
    />
  );
  const renderMarkPop = (group: string, current: string | undefined, apply: (c?: string) => void) => {
    if (markPop?.group !== group) return null;
    const favs = [...new Set([...(personMarks || []), ...(current ? [current] : [])])];
    return (
      <div
        data-patch-popover
        data-patch-popover-for={group}
        style={{ position: 'fixed', zIndex: 9999, top: markPop.y, left: markPop.x, background: 'var(--surface, #211f1a)', border: '1px solid var(--border)', borderRadius: 8, padding: 10, boxShadow: '0 8px 24px rgba(0,0,0,.35)', minWidth: 180 }}
      >
        {favs.length > 0 && (
          // Many favourites (user 23.10: 15+ wanted): wrap + max width — the
          // popover grows vertically, the viewport clamp re-basculates it.
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 208, marginBottom: 8 }}>
            {favs.map((c) => (
              <span key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                <button type="button" title={t('instrumentEditor.patchMarkFavTitle')} onClick={() => { apply(c); setMarkPop(null); }} style={{ width: 20, height: 20, borderRadius: 4, border: current?.toLowerCase() === c ? '2px solid var(--text)' : '1px solid var(--border)', background: c, cursor: 'pointer', padding: 0 }} />
                {onRenameFavourite && (
                  <input
                    type="color"
                    value={c}
                    title={t('instrumentEditor.patchMarkRenameTitle')}
                    onChange={(e) => onRenameFavourite(c, e.target.value)}
                    style={{ width: 12, height: 20, padding: 0, border: '1px solid var(--border)', borderRadius: 3, background: 'transparent', cursor: 'pointer' }}
                  />
                )}
              </span>
            ))}
          </div>
        )}
        <input type="color" value={current || '#888888'} onChange={(e) => apply(e.target.value)} style={{ width: '100%', height: 32, padding: 0, border: '1px solid var(--border)', borderRadius: 4, background: 'transparent', cursor: 'pointer' }} />
        <div style={{ marginTop: 6, textAlign: 'center' }}>
          <button type="button" className="btn btn-small btn-secondary" onClick={() => { apply(undefined); setMarkPop(null); }}>{t('instrumentEditor.patchMarkClear')}</button>
        </div>
      </div>
    );
  };
  const current: IemChoice =
    iemType === 'stereo' || iemType === 'mono' ? iemType : 'none';
  const hasIem = current !== 'none';
  const hasWedges = wedges > 0;
  const stereoRelevant = hasWedges && wedges > 1;

  const rackSelect = (
    value: string | undefined,
    set: (v: string) => void,
    placeholder = '—'
  ) => (
    <select
      className="form-input"
      value={value || ''}
      onChange={(e) => set(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {racks.map((r) => (
        <option key={r.id} value={r.id}>{r.name}</option>
      ))}
    </select>
  );

  // Output-channel picker on the given rack: grey out channels already used
    // by another person's monitor output. When `pairLabel` is set (stereo IEM),
    // each option shows the L/R pair ("7-8") and is greyed if EITHER channel of
    // the pair is taken (or the pair would run past the rack's capacity).
    // Replacement/guest rule: a channel occupied by someone who plays the SAME
    // instrument as the person being edited is treated as free (they never play
    // at the same time). For stereo pairs, only NON-OVERLAPPING pairs are shown:
    // a pair whose start channel is already occupied (by anyone, including the
    // edited person's own other output) is filtered out, so after assigning 1-2
    // the next offered pair is 3-4, never 2-3.
    const outputChannelSelect = (
      rackId: string | undefined,
      channel: number | undefined,
      set: (v: number) => void,
      label: string,
      pairLabel = false,
    ) => {
      const rack = racks.find((r) => r.id === rackId);
      const { outputs } = rackCapacities(rack);
      const taken = (ch: number) =>
        isChannelTakenForOutput(usage, 'outputs', rackId || '', ch, rackId, channel, editedInstruments, personInstruments, editedTags, personTags,
          // Monitor outputs keep the automatic replacement/guest sharing (a dep
          // reuses the main player's IEM/wedges) — only the self-exclusion is new.
          personLabel, true);
      const options: { ch: number; blocked: boolean; display: string }[] = [];
      for (let ch = 1; ch <= outputs; ch++) {
        if (pairLabel) {
          const pairEnds = ch + 1;
          const isCurrentPair = ch === channel;
          const runsPast = pairEnds > outputs;
          // A stereo pair occupies BOTH ch and ch+1. Show EVERY pair for a global
          // view; a pair is greyed (blocked) when its start or end channel is
          // taken by a non-shared occupant, or it runs past the rack capacity.
          const startTaken = !isCurrentPair && !!taken(ch);
          const endTaken = !isCurrentPair && !!taken(pairEnds);
          const blocked = startTaken || endTaken || runsPast;
          const occ = isCurrentPair ? undefined : (taken(ch) || taken(pairEnds));
          const display = runsPast
                      ? `${ch} (needs ${pairEnds})`
                      : `${ch}-${pairEnds}${occ ? ` (${occ})` : ''}`;
          options.push({ ch, blocked, display });
        } else {
          const occ = taken(ch);
          options.push({ ch, blocked: !!occ, display: occ ? `${ch} (${occ})` : `${ch}` });
        }
      }
      return (
        <select
          className="form-input"
          value={channel || 0}
          onChange={(e) => set(parseInt(e.target.value) || 0)}
          title={t('monitoring.greyedTitle', { label })}
        >
          <option value={0}>—</option>
          {options.map((o) => (
            <option key={o.ch} value={o.ch} disabled={o.blocked}>
              {o.display}
            </option>
          ))}
        </select>
      );
    };

  return (
    <fieldset className="form-group" style={{ border: 'none', padding: 0, margin: 0 }}>
      <legend style={{ padding: 0, marginBottom: 8 }}>{t('monitoring.title')}</legend>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        {IEM_OPTIONS.map((opt) => {
          const selected = current === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange({ iemType: opt.value })}
              style={{
                border: `1px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 10,
                padding: '8px 14px',
                background: selected ? 'var(--accent-muted)' : 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{t(opt.labelKey)}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>{t(opt.hintKey)}</div>
            </button>
          );
        })}
      </div>

      {/* Wired in-ears: the feed comes from the monitor rack, so there is NO
          transmitter / bodypack to ask the promoter for. The WHO-provides
          selector (2026-10-17, user): the wired pack can be band-provided
          (default — the band's console) or venue-provided. */}
      {hasIem && (
        <div style={{ marginBottom: 12 }}>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={iemWired || false}
              onChange={(e) => onChange({ iemWired: e.target.checked })}
            />
            <span style={{ marginLeft: 6 }}>{t('monitoring.wired')}</span>
          </label>
          {iemWired && (
            <div style={{ marginLeft: 24, marginTop: 6, display: 'flex', gap: 14 }}>
              {(['band', 'venue'] as const).map((pv) => (
                <label key={pv} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <input
                    type="radio"
                    name={`iem-wired-prov-${iemType}`}
                    checked={(iemWiredProvidedBy || 'band') === pv}
                    onChange={() => onChange({ iemWiredProvidedBy: pv })}
                  />
                  <span>{pv === 'band' ? t('pdf.misc.band') : t('pdf.misc.venue')}</span>
                </label>
              ))}
            </div>
          )}
          <small style={{ color: 'var(--text-dim)', display: 'block', marginTop: 2 }}>{t('monitoring.wiredHint')}</small>
        </div>
      )}

      {/* IEM output routing */}
      {hasIem && (
        <div className="form-row" style={{ marginBottom: 8 }}>
          <div className="form-group">
            <label>{t('monitoring.iemOutRack')}</label>
            {rackSelect(iemRackId, (v) => onChange({ iemRackId: v }), t('monitoring.rackPh'))}
          </div>
          <div className="form-group">
            <label>{current === 'stereo' ? t('monitoring.channelLR') : t('monitoring.channel')}</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {outputChannelSelect(iemRackId, iemOutStart, (v) => onChange({ iemOutStart: v }), t('monitoring.iemChannel'), current === 'stereo')}
              {markSwatch('iem', iemPatchMark)}
            </div>
            {current === 'stereo' && iemOutStart ? (
              <small style={{ color: 'var(--text-dim)', display: 'block', marginTop: 2 }}>
                L = {iemOutStart} · R = {iemOutStart + 1}
              </small>
            ) : null}
          </div>
        </div>
      )}
      {hasIem && renderMarkPop('iem', iemPatchMark, (c) => onChange({ iemPatchMark: c }))}

      <div className="form-row">
        <div className="form-group">
          <label>{t('monitoring.floorWedges')}</label>
          <select
            className="form-input"
            value={wedges}
            onChange={(e) => onChange({ wedges: Number(e.target.value) })}
          >
            {WEDGE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>

        <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 6 }}>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={subwoofer}
              onChange={(e) => onChange({ subwoofer: e.target.checked })}
            />
            <span style={{ marginLeft: 6 }}>{t('monitoring.subwoofer')}</span>
          </label>
        </div>
      </div>

      {/* Wedge output routing */}
      {hasWedges && (
        <div className="form-row" style={{ marginBottom: 8 }}>
          <div className="form-group">
            <label>{t('monitoring.wedgeOutRack')}</label>
            {rackSelect(wedgeRackId, (v) => onChange({ wedgeRackId: v }), t('monitoring.rackPh'))}
          </div>
          <div className="form-group">
            <label>{t('monitoring.startChannel')}</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {outputChannelSelect(wedgeRackId, wedgeOutStart, (v) => onChange({ wedgeOutStart: v }), t('monitoring.wedgeStartChannel'), wedgesStereo)}
              {markSwatch('wedges', wedgePatchMark)}
            </div>
          </div>
          {stereoRelevant && (
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 6 }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={wedgesStereo || false}
                  onChange={(e) => onChange({ wedgesStereo: e.target.checked })}
                />
                <span style={{ marginLeft: 6 }}>{t('monitoring.stereoLR')}</span>
              </label>
            </div>
          )}
        </div>
      )}

      {hasWedges && renderMarkPop('wedges', wedgePatchMark, (c) => onChange({ wedgePatchMark: c }))}

      {/* Subwoofer output routing */}
      {subwoofer && (
        <div className="form-row" style={{ marginBottom: 8 }}>
          <div className="form-group">
            <label>{t('monitoring.subOutRack')}</label>
            {rackSelect(subRackId, (v) => onChange({ subRackId: v }), t('monitoring.rackPh'))}
          </div>
          <div className="form-group">
            <label>{t('monitoring.channel')}</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {outputChannelSelect(subRackId, subOutStart, (v) => onChange({ subOutStart: v }), t('monitoring.subChannel'))}
              {markSwatch('sub', subPatchMark)}
            </div>
          </div>
        </div>
      )}
      {subwoofer && renderMarkPop('sub', subPatchMark, (c) => onChange({ subPatchMark: c }))}
    </fieldset>
  );
}
